import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { logToSentry } from '@jsmdg/react-fragment-scripts/fragment';
import { createIconComponent, Image, Loader, LoaderSize, PureButton } from '@jsmdg/yoshi';
import { ReactComponent as ControlsPauseIcon } from '../../assets/icons/controls-pause.svg';
import { ReactComponent as ControlsPlayIcon } from '../../assets/icons/controls-play.svg';
import { ReactComponent as CursorSelectFrameIcon } from '../../assets/icons/cursor-select-frame.svg';
import { ReactComponent as VolumeControlOffIcon } from '../../assets/icons/volume-control-off.svg';
import { toIsoDuration } from '../../helper/toIsoDuration';
import { useProduct } from '../../hooks/useProduct';
import {
    type FullscreenDocument,
    type FullscreenElement,
    type FullscreenVideoElement,
    type VideoProps,
} from '../../types/videoTypes';
import { PlayButton } from './PlayButton';
import { startVideo } from './videoGallery.tracking';
import styles from './Video.module.scss';

function isInFullscreen(doc: FullscreenDocument): boolean {
    return Boolean(
        doc.fullscreenElement || doc.webkitFullscreenElement || doc.mozFullScreenElement,
    );
}

async function exitFullscreen(doc: FullscreenDocument): Promise<void> {
    if (doc.exitFullscreen) {
        await doc.exitFullscreen();
        return;
    }

    if (doc.webkitExitFullscreen) {
        await doc.webkitExitFullscreen();
        return;
    }

    if (doc.mozCancelFullScreen) {
        await doc.mozCancelFullScreen();
    }
}

async function requestFullscreen(element: FullscreenElement): Promise<void> {
    if (element.requestFullscreen) {
        await element.requestFullscreen();
        return;
    }

    if (element.webkitRequestFullscreen) {
        await element.webkitRequestFullscreen();
        return;
    }

    if (element.mozRequestFullScreen) {
        await element.mozRequestFullScreen();
    }
}

const messages = defineMessages({
    playIconAriaLabel: {
        defaultMessage: 'Video abspielen',
    },
    playLabel: {
        defaultMessage: 'Wiedergabe',
    },
    pauseLabel: {
        defaultMessage: 'Pause',
    },
    muteLabel: {
        defaultMessage: 'Stumm',
    },
    unmuteLabel: {
        defaultMessage: 'Ton an',
    },
    fullscreenLabel: {
        defaultMessage: 'Vollbild',
    },
    progressLabel: {
        defaultMessage: 'Video Fortschritt',
    },
    loadingLabel: {
        defaultMessage: 'Video wird geladen',
    },
});

function formatTime(seconds: number): string {
    if (!Number.isFinite(seconds) || seconds < 0) {
        return '0:00';
    }

    const total = Math.floor(seconds);
    const mins = Math.floor(total / 60);
    const secs = `${total % 60}`.padStart(2, '0');

    return `${mins}:${secs}`;
}

function stopEventPropagation(event: { stopPropagation: () => void }): void {
    event.stopPropagation();
}

const PlayIcon = createIconComponent(ControlsPlayIcon);
const PauseIcon = createIconComponent(ControlsPauseIcon);
const VolumeOffIcon = createIconComponent(VolumeControlOffIcon);
const FullscreenIcon = createIconComponent(CursorSelectFrameIcon);

const Video = ({
    ariaLabel,
    className,
    controls = true,
    durationInSeconds,
    height,
    index,
    onFullscreenChange,
    playsInline = true,
    poster,
    sources,
    thumbnailAlt,
    width,
    withImagePreview,
}: VideoProps): JSX.Element => {
    const intl = useIntl();
    const product = useProduct();
    const [isActivated, setIsActivated] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isVideoLoading, setIsVideoLoading] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [isPortraitVideo, setIsPortraitVideo] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);

    function togglePlayPause(): void {
        if (!videoRef.current) {
            return;
        }

        if (videoRef.current.paused) {
            void videoRef.current.play();
            return;
        }

        videoRef.current.pause();
    }

    function activateAndPlay(): void {
        setIsActivated(true);
        setIsVideoLoading(true);
        videoRef.current?.play();
    }

    function handleVideoSurfaceClick(): void {
        if (!isActivated) {
            activateAndPlay();
            return;
        }

        togglePlayPause();
    }

    async function handleToggleFullscreen(): Promise<void> {
        const doc = document as FullscreenDocument;
        const wrapper = wrapperRef.current as FullscreenElement | null;
        const video = videoRef.current as FullscreenVideoElement | null;

        if (!wrapper || !video) {
            return;
        }

        if (video.webkitEnterFullscreen) {
            if (video.webkitDisplayingFullscreen && video.webkitExitFullscreen) {
                await video.webkitExitFullscreen();
                return;
            }

            await video.webkitEnterFullscreen();
            return;
        }

        if (isInFullscreen(doc)) {
            await exitFullscreen(doc);
            return;
        }

        await requestFullscreen(wrapper);
    }

    const interactiveVideoProps = !isActivated
        ? {
              role: 'button' as const,
              tabIndex: 0,
              onKeyDown: (event: KeyboardEvent<HTMLVideoElement>) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleVideoSurfaceClick();
                  }
              },
          }
        : {};

    useEffect(() => {
        const video = videoRef.current as FullscreenVideoElement | null;

        const syncFullscreenState = (): void => {
            const doc = document as Document & {
                webkitFullscreenElement?: Element | null;
                mozFullScreenElement?: Element | null;
            };
            const fullscreenElement =
                doc.fullscreenElement || doc.webkitFullscreenElement || doc.mozFullScreenElement;
            const isVideoFullscreen = Boolean(video?.webkitDisplayingFullscreen);

            setIsFullscreen(
                fullscreenElement === wrapperRef.current ||
                    fullscreenElement === videoRef.current ||
                    isVideoFullscreen,
            );
        };

        document.addEventListener('fullscreenchange', syncFullscreenState);
        document.addEventListener('webkitfullscreenchange', syncFullscreenState as EventListener);
        document.addEventListener('mozfullscreenchange', syncFullscreenState as EventListener);
        video?.addEventListener('webkitbeginfullscreen', syncFullscreenState as EventListener);
        video?.addEventListener('webkitendfullscreen', syncFullscreenState as EventListener);

        syncFullscreenState();

        return () => {
            document.removeEventListener('fullscreenchange', syncFullscreenState);
            document.removeEventListener(
                'webkitfullscreenchange',
                syncFullscreenState as EventListener,
            );
            document.removeEventListener(
                'mozfullscreenchange',
                syncFullscreenState as EventListener,
            );
            video?.removeEventListener(
                'webkitbeginfullscreen',
                syncFullscreenState as EventListener,
            );
            video?.removeEventListener('webkitendfullscreen', syncFullscreenState as EventListener);
        };
    }, []);

    useEffect(() => {
        const video = videoRef.current;

        if (!video || !isActivated) {
            return () => undefined;
        }

        const syncState = (): void => {
            setCurrentTime(video.currentTime || 0);
            setIsPlaying(!video.paused && !video.ended);
        };

        video.addEventListener('timeupdate', syncState);
        video.addEventListener('play', syncState);
        video.addEventListener('pause', syncState);
        video.addEventListener('volumechange', syncState);

        syncState();

        return () => {
            video.removeEventListener('timeupdate', syncState);
            video.removeEventListener('play', syncState);
            video.removeEventListener('pause', syncState);
            video.removeEventListener('volumechange', syncState);
        };
    }, [isActivated]);

    useEffect(() => {
        onFullscreenChange?.(isFullscreen);
    }, [isFullscreen, onFullscreenChange]);

    return (
        <div className={classNames('position-relative', styles.wrapper)} ref={wrapperRef}>
            <Image
                role="presentation"
                className={classNames(
                    'w-100 h-100 d-block position-absolute',
                    styles.carouselImage,
                    styles.thumbnail,
                    isActivated && !isVideoLoading && 'd-none',
                    withImagePreview && 'rounded-2',
                )}
                src={poster.src}
                alt={`${thumbnailAlt} - ${index + 1}`}
                srcSet={poster.srcset}
                sizes={poster.sizes}
                lazyLoad={false}
                fetchPriority={index === 0 ? 'high' : 'low'}
                width={width}
                height={height}
            />
            <video
                ref={videoRef}
                className={classNames(className, styles.video, {
                    [styles.portraitVideo]: isPortraitVideo,
                })}
                aria-label={ariaLabel}
                preload={isActivated ? 'metadata' : 'none'}
                playsInline={playsInline}
                muted
                width={width}
                height={height}
                controls={false}
                onClick={() => handleVideoSurfaceClick()}
                onLoadedMetadata={event => {
                    const { videoHeight, videoWidth } = event.currentTarget;
                    setIsPortraitVideo(videoHeight > videoWidth);
                }}
                onCanPlay={() => {
                    setIsVideoLoading(false);
                }}
                onPlay={() => {
                    startVideo(toIsoDuration(durationInSeconds));
                    setIsVideoLoading(false);
                    if (!isActivated) {
                        setIsActivated(true);
                    }
                }}
                onWaiting={() => {
                    setIsVideoLoading(true);
                }}
                {...interactiveVideoProps}
            >
                <track kind="captions" />
                {sources.map(source => (
                    <source
                        key={`${source.src}-${source.type}`}
                        src={source.src}
                        type={source.type}
                        onError={() => {
                            logToSentry(new Error('Failed to download video'), {
                                productId: product.productId,
                                src: source.src,
                                type: source.type,
                            });
                        }}
                    />
                ))}
            </video>
            {isActivated && isVideoLoading && (
                <div
                    className={styles.loadingOverlay}
                    role="status"
                    aria-live="polite"
                    aria-label={intl.formatMessage(messages.loadingLabel)}
                >
                    <Loader loaderSize={LoaderSize.Large} />
                    <span className="screenReaderOnly">
                        {intl.formatMessage(messages.loadingLabel)}
                    </span>
                </div>
            )}
            {!isActivated && (
                <PlayButton
                    noHover={false}
                    onClickHandler={event => {
                        event.stopPropagation();
                        event.preventDefault();
                        activateAndPlay();
                    }}
                    a11yText={`${ariaLabel} ${intl.formatMessage(messages.playIconAriaLabel)}`}
                />
            )}
            {/* Video controls */}
            {isActivated && controls && (
                <div
                    className={classNames(styles.controlsBar, {
                        [styles.controlsBarFullscreen]: isFullscreen,
                    })}
                    onPointerDownCapture={stopEventPropagation}
                    onPointerMoveCapture={stopEventPropagation}
                    onPointerUpCapture={stopEventPropagation}
                    onTouchStartCapture={stopEventPropagation}
                    onTouchMoveCapture={stopEventPropagation}
                    onTouchEndCapture={stopEventPropagation}
                    onMouseDownCapture={stopEventPropagation}
                    onMouseMoveCapture={stopEventPropagation}
                    onMouseUpCapture={stopEventPropagation}
                >
                    <PureButton
                        className={styles.controlIcon}
                        aria-label={intl.formatMessage(
                            isPlaying ? messages.pauseLabel : messages.playLabel,
                        )}
                        onClick={() => togglePlayPause()}
                    >
                        {isPlaying ? (
                            <PauseIcon
                                className={styles.controlIcon}
                                ariaLabel={intl.formatMessage(messages.pauseLabel)}
                            />
                        ) : (
                            <PlayIcon
                                className={styles.controlIcon}
                                ariaLabel={intl.formatMessage(messages.playLabel)}
                            />
                        )}
                    </PureButton>
                    <span className={styles.timeLabel}>
                        {formatTime(currentTime)} / {formatTime(durationInSeconds)}
                    </span>
                    <input
                        type="range"
                        className={styles.progress}
                        min={0}
                        max={durationInSeconds || 0}
                        step={0.1}
                        value={Math.min(currentTime, durationInSeconds || 0)}
                        aria-label={intl.formatMessage(messages.progressLabel)}
                        onChange={event => {
                            if (!videoRef.current) {
                                return;
                            }

                            const nextTime = Number(event.target.value);
                            videoRef.current.currentTime = nextTime;
                            setCurrentTime(nextTime);
                        }}
                    />
                    <PureButton
                        className={styles.controlIcon}
                        aria-label={intl.formatMessage(messages.unmuteLabel)}
                    >
                        <VolumeOffIcon
                            className={styles.controlIcon}
                            ariaLabel={intl.formatMessage(messages.unmuteLabel)}
                        />
                    </PureButton>
                    <PureButton
                        className={styles.controlIcon}
                        aria-label={intl.formatMessage(messages.fullscreenLabel)}
                        onClick={async () => {
                            await handleToggleFullscreen();
                        }}
                    >
                        <FullscreenIcon
                            className={styles.controlIcon}
                            ariaLabel={intl.formatMessage(messages.fullscreenLabel)}
                        />
                    </PureButton>
                </div>
            )}
        </div>
    );
};

export { Video };
